import { Suspense, forwardRef, lazy, useMemo, useCallback } from 'react';
import styled from 'styled-components';
import noop from 'lodash/noop';
import type { FroalaOptions } from '@iqx-web/libs/froala-editor';
import { defaultConfig } from './default-config';
import { Skeleton } from '../ui';

type EditorProps = {
  disabled?: boolean;
  onChange?: (htmlText: string | object) => void;
  value?: string;
  config?: Partial<FroalaOptions>;
};

const skeletonParams = {
  active: true,
  paragraph: { rows: 3 },
};

const DisableFroala = styled.div<EditorProps>`
  ${({ disabled }) =>
    disabled &&
    `pointer-events: none; 
     cursor: not-allowed; 
     color: rgba(0, 0, 0, 0.25);
     background-color light-dark(rgba(239, 239, 239, 0.3), rgba(59, 59, 59, 0.3));
    `}

  ${({ config }) => {
    if (!config || !config.toolbarInline) return '';

    return `border: 1px solid #d9d9d9;
       padding: 3px 7px;
       border-radius: 2px;`;
  }}
`;

const lazyLoadEditor = async (pluginsEnabled?: FroalaOptions['pluginsEnabled']) => {
  const { importFroalaEditorPlugins } = await import('@iqx-web/libs/froala-editor');
  await Promise.allSettled(importFroalaEditorPlugins(pluginsEnabled));

  return import('./FroalaEditor.styles');
};

export function withFroalaEditorHoc(staticConfig: Partial<FroalaOptions>) {
  const froalaConfig = { ...defaultConfig, ...staticConfig };
  const Editor = lazy(() => lazyLoadEditor(froalaConfig.pluginsEnabled));

  return forwardRef<unknown, EditorProps>(
    (
      { value = '', onChange = noop, disabled = false, config: customConfig, ...rest }: EditorProps,
      ref: unknown,
    ) => {
      const config = useMemo(() => ({ ...froalaConfig, ...customConfig }), [customConfig]);
      // Bug: Froala fires value change on init.
      const _onChange = useCallback(
        (v: string) => {
          if (v !== value) {
            onChange?.(v);
          }
        },
        [onChange, value],
      );

      return (
        <Suspense fallback={<Skeleton {...skeletonParams} />}>
          <DisableFroala disabled={disabled} config={config}>
            <Editor ref={ref} config={config} model={value} onModelChange={_onChange} {...rest} />
          </DisableFroala>
        </Suspense>
      );
    },
  );
}
